import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  Alert, Tooltip, CircularProgress, Paper, Stack, Tabs, Tab, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import { Add, Delete, Refresh, Shield, ShieldOutlined, Edit, Block, LockOpen, GppBad } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../../services/api";

interface AclEntry {
  id: number;
  ip_addr: string;
  mask: number;
  port: number;
  description: string;
  tag: string;
  active: boolean;
  created_at: string;
}

interface AclStatus {
  total: number;
  active: number;
  acl_enforced: boolean;
}

const defaultForm = { ip_addr: "", mask: 32, port: 0, description: "", tag: "" };

export const AclPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [entries, setEntries] = useState<AclEntry[]>([]);
  const [status, setStatus] = useState<AclStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  // ── Blacklist / banned IPs ──
  const [tab, setTab] = useState(0);
  const [banned, setBanned] = useState<any[]>([]);
  const [banLoading, setBanLoading] = useState(false);
  const [unbanning, setUnbanning] = useState("");
  const [banForm, setBanForm] = useState({ ip: "", jail: "recidive" });

  const loadBanned = useCallback(async () => {
    setBanLoading(true);
    try { const { data } = await api.get("/acl/banned"); setBanned(data.entries || []); }
    catch { enqueueSnackbar("Failed to load banned IPs", { variant: "error" }); }
    finally { setBanLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { if (tab === 1) loadBanned(); }, [tab, loadBanned]);

  const handleUnban = async (ip: string, jail?: string) => {
    setUnbanning(ip + (jail || ""));
    try {
      const { data } = await api.post("/acl/unban", { ip, jail });
      enqueueSnackbar(data.unbanned ? `Unbanned ${ip}` : `No active ban for ${ip}`, { variant: data.unbanned ? "success" : "info" });
      loadBanned();
    } catch (e: any) { enqueueSnackbar(e.response?.data?.message || "Unban failed", { variant: "error" }); }
    finally { setUnbanning(""); }
  };

  const handleManualBan = async () => {
    if (!banForm.ip) { enqueueSnackbar("IP required", { variant: "warning" }); return; }
    try {
      const { data } = await api.post("/acl/ban", banForm);
      enqueueSnackbar(data.banned ? `Blocked ${banForm.ip} in ${data.jail}` : `Ban failed: ${data.error || ""}`, { variant: data.banned ? "success" : "error" });
      setBanForm({ ip: "", jail: "recidive" });
      loadBanned();
    } catch (e: any) { enqueueSnackbar(e.response?.data?.message || "Ban failed", { variant: "error" }); }
  };

  const load = useCallback(async () => {
    try {
      const [listRes, statusRes] = await Promise.all([
        api.get("/acl"),
        api.get("/acl/status"),
      ]);
      setEntries(listRes.data);
      setStatus(statusRes.data);
    } catch {
      enqueueSnackbar("Failed to load ACL data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm(defaultForm); setOpen(true); };
  const openEdit = (e: AclEntry) => {
    setEditId(e.id);
    setForm({ ip_addr: e.ip_addr, mask: e.mask, port: e.port, description: e.description || "", tag: e.tag || "" });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.ip_addr) { enqueueSnackbar("IP address is required", { variant: "warning" }); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/acl/${editId}`, form);
        enqueueSnackbar("ACL entry updated", { variant: "success" });
      } else {
        await api.post("/acl", form);
        enqueueSnackbar("ACL entry added", { variant: "success" });
      }
      setOpen(false);
      load();
    } catch (e: any) {
      enqueueSnackbar(e.response?.data?.message || "Save failed", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (e: AclEntry) => {
    try {
      await api.put(`/acl/${e.id}`, { active: !e.active });
      enqueueSnackbar(`Entry ${!e.active ? "enabled" : "disabled"}`, { variant: "info" });
      load();
    } catch {
      enqueueSnackbar("Update failed", { variant: "error" });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this ACL entry?")) return;
    try {
      await api.delete(`/acl/${id}`);
      enqueueSnackbar("Entry deleted", { variant: "success" });
      load();
    } catch {
      enqueueSnackbar("Delete failed", { variant: "error" });
    }
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      const res = await api.post("/acl/reload");
      enqueueSnackbar(res.data.message, { variant: res.data.reloaded ? "success" : "warning" });
      load();
    } catch {
      enqueueSnackbar("Reload failed", { variant: "error" });
    } finally {
      setReloading(false);
    }
  };

  const cidr = (e: AclEntry) => e.mask === 32 ? e.ip_addr : `${e.ip_addr}/${e.mask}`;

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={700}>SIP Access Control</Typography>
        <Typography variant="body2" color="text.secondary">
          Whitelist (which IPs may send SIP to the SBC) and blacklist (IPs jailed by fail2ban / anti-flood).
        </Typography>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab icon={<Shield />} iconPosition="start" label="Whitelist" />
        <Tab icon={<GppBad />} iconPosition="start" label="Blacklist / Banned IPs" />
      </Tabs>

      {tab === 0 && (
      <>
      <Stack direction="row" justifyContent="flex-end" gap={1} mb={2}>
        <Tooltip title="Sync ACL with Kamailio">
          <Button variant="outlined" startIcon={reloading ? <CircularProgress size={16} /> : <Refresh />} onClick={handleReload} disabled={reloading}>
            Sync Kamailio
          </Button>
        </Tooltip>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add IP</Button>
      </Stack>

      {/* Status Cards */}
      <Stack direction="row" gap={2} mb={3}>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" fontWeight={700} color="primary">{status?.active ?? 0}</Typography>
          <Typography variant="body2" color="text.secondary">Active Entries</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" fontWeight={700}>{status?.total ?? 0}</Typography>
          <Typography variant="body2" color="text.secondary">Total Entries</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 2, display: "flex", alignItems: "center", gap: 2 }}>
          {status?.acl_enforced ? (
            <>
              <Shield color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color="error.main">ACL ENFORCED</Typography>
                <Typography variant="body2" color="text.secondary">Only listed IPs can send SIP traffic</Typography>
              </Box>
            </>
          ) : (
            <>
              <ShieldOutlined color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color="success.main">OPEN MODE</Typography>
                <Typography variant="body2" color="text.secondary">All IPs allowed — add entries to enable ACL</Typography>
              </Box>
            </>
          )}
        </Paper>
      </Stack>

      {entries.length === 0 ? (
        <Alert severity="info" icon={<ShieldOutlined />}>
          No ACL entries. All SIP traffic is currently allowed. Add IPs to start enforcing the whitelist.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>IP / CIDR</TableCell>
                <TableCell>Port</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(e => (
                <TableRow key={e.id} hover sx={{ opacity: e.active ? 1 : 0.5 }}>
                  <TableCell><Typography fontFamily="monospace" fontWeight={600}>{cidr(e)}</Typography></TableCell>
                  <TableCell>{e.port === 0 ? <Typography color="text.secondary" variant="body2">Any</Typography> : e.port}</TableCell>
                  <TableCell>{e.description || "—"}</TableCell>
                  <TableCell>{e.tag ? <Chip label={e.tag} size="small" /> : "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={e.active ? "Active" : "Disabled"}
                      color={e.active ? "success" : "default"}
                      size="small"
                      onClick={() => handleToggle(e)}
                      sx={{ cursor: "pointer" }}
                    />
                  </TableCell>
                  <TableCell>{new Date(e.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(e)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(e.id)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
      )}

      {/* ── Blacklist / Banned IPs tab ── */}
      {tab === 1 && (
      <>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
          <Paper variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <TextField size="small" label="Block an IP" placeholder="e.g. 45.148.10.141" value={banForm.ip}
              onChange={e => setBanForm(f => ({ ...f, ip: e.target.value }))} sx={{ width: 200 }} />
            <TextField size="small" select label="Jail" value={banForm.jail}
              onChange={e => setBanForm(f => ({ ...f, jail: e.target.value }))} sx={{ width: 180 }}>
              {["recidive", "sshd", "kamailio-dos", "nginx-limit-req", "nginx-botsearch", "nginx-http-auth", "callessential-api"].map(j =>
                <MenuItem key={j} value={j}>{j}</MenuItem>)}
            </TextField>
            <Button variant="contained" color="error" startIcon={<Block />} onClick={handleManualBan}>Block</Button>
          </Paper>
          <Button variant="outlined" startIcon={banLoading ? <CircularProgress size={16} /> : <Refresh />} onClick={loadBanned} disabled={banLoading}>Refresh</Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          IPs currently jailed by fail2ban (each jail = the reason) and the Kamailio anti-flood ban list.
          Unban to release an IP that was blocked for a specific reason. Whitelisted IPs are never jailed.
        </Alert>

        {banLoading ? <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        : banned.length === 0 ? (
          <Alert severity="success" icon={<LockOpen />}>No IPs are currently banned.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>IP Address</TableCell>
                <TableCell>Jailed In (reason)</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {banned.map((b: any) => (
                  <TableRow key={b.ip} hover>
                    <TableCell><Typography fontFamily="monospace" fontWeight={600}>{b.ip}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {b.jails.map((j: string) => (
                          <Tooltip key={j} title={`Unban from ${j} only`}>
                            <Chip size="small" color="error" variant="outlined" label={j}
                              onDelete={() => handleUnban(b.ip, j)} />
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>{(b.sources || []).join(", ")}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" color="success"
                        startIcon={unbanning === b.ip ? <CircularProgress size={14} /> : <LockOpen />}
                        disabled={unbanning.startsWith(b.ip)} onClick={() => handleUnban(b.ip)}>
                        Unban (all)
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? "Edit ACL Entry" : "Add IP to ACL"}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              label="IP Address"
              value={form.ip_addr}
              onChange={e => setForm(f => ({ ...f, ip_addr: e.target.value }))}
              placeholder="e.g. 192.168.1.100 or 10.0.0.0"
              required
              fullWidth
            />
            <Stack direction="row" gap={2}>
              <TextField
                label="CIDR Mask"
                type="number"
                value={form.mask}
                onChange={e => setForm(f => ({ ...f, mask: parseInt(e.target.value) || 32 }))}
                inputProps={{ min: 0, max: 32 }}
                helperText="0-32. Use 32 for single host"
                fullWidth
              />
              <TextField
                label="Port"
                type="number"
                value={form.port}
                onChange={e => setForm(f => ({ ...f, port: parseInt(e.target.value) || 0 }))}
                helperText="0 = any port"
                fullWidth
              />
            </Stack>
            <TextField
              label="Description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Office SIP trunk, Carrier gateway"
              fullWidth
            />
            <TextField
              label="Tag (optional)"
              value={form.tag}
              onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
              placeholder="e.g. carrier, office, pbx"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : (editId ? "Update" : "Add to ACL")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
